import { dataVizVariableNames, resolveDataVizData } from '@agavi/blocks';
import { BarChart3, ChartScatter, LineChart, Upload, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useArmedDelete } from './block-actions';
import { BlockCard, BlockPanelCard } from './block-card';
import { type BlockIssue, findDuplicateGroups, IssueBar, type IssueContext, issueFieldIds, useIssueContext } from './block-issues';
import { BlockNameField } from './block-name-field';
import { DataVizCanvas } from './dataviz-canvas';
import { DataVizCsvImport, MAX_CSV_POINTS_PER_SERIES, MAX_CSV_SERIES } from './dataviz-csv-import';
import { DataVizDisplay } from './dataviz-display';
import { DataVizGrid } from './dataviz-grid';
import { chartOrientation, orientationIdsFor } from './dataviz-orientation';
import { DataVizSonificationPlayer } from './dataviz-sonification-player';
import { isNumericXAxis, isoDateInputToMs, msToIsoDateInput } from './dataviz-utils';
import { useIsMobile } from './task-content';
import { DisplayBankRow, DisplayScenarioBar, useDisplayPhoneTest, useDisplayVersions } from './use-display-versions';
import { useRestoredData } from './use-restored-data';
import { hasRollableVariables, probeVariableValues, rollVariableValues } from './variable-chip-preview';

import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block } from '@/types/experience';
import type { ChartType, DataVizBlockData } from '@agavi/blocks';

import Toast from '@/components/toast';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { DateField } from '@/components/ui/date-field';
import { NumberField } from '@/components/ui/number-field';
import SelectMenu from '@/components/ui/select-menu';
import { TextField } from '@/components/ui/text-field';
import { useTaskPreviewRoll, useTaskVariables } from '@/contexts/task-variables-context';
import { isNameTaken } from '@/lib/block-naming';

interface DataVizBlockProps {
  block: Block;
  isPreview: boolean;
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

/**
 * What VARIES between scenarios, and what is shared — §3.2's split.
 *
 * The DATA is the content, so `series` travels with the variant, and `title` with it: a chart
 * whose numbers changed while its title did not would be mislabelled. `sonificationSeriesId`
 * and `sonificationThresholds` travel too, because both POINT AT the data — an id naming a
 * series that only exists in another variant is a dangling reference, and a threshold is a
 * value on that variant's own scale.
 *
 * `chartType`, the axis labels, `xAxisType` and `dataTableDisplay` stay on the block: they are
 * how a chart is DRAWN rather than what it shows, and a group that changed shape as it stepped
 * would be the layout moving rather than the scenario.
 */
const DATAVIZ_VERSION_KEYS = ['series', 'title', 'sonificationSeriesId', 'sonificationThresholds'] as const;

function toData(raw: Record<string, unknown>): DataVizBlockData {
  const series = raw.series as DataVizBlockData['series'] | undefined;
  return {
    chartType: (raw.chartType as ChartType | undefined) ?? 'line',
    title: raw.title as string | undefined,
    xAxisLabel: raw.xAxisLabel as string | undefined,
    yAxisLabel: raw.yAxisLabel as string | undefined,
    series: series && series.length > 0 ? series : [{ id: crypto.randomUUID(), label: '', points: [] }],
    sonificationSeriesId: raw.sonificationSeriesId as string | undefined,
    dataTableDisplay: raw.dataTableDisplay as DataVizBlockData['dataTableDisplay'],
    xAxisType: raw.xAxisType as DataVizBlockData['xAxisType'],
    sonificationThresholds: raw.sonificationThresholds as DataVizBlockData['sonificationThresholds'],
  };
}

const CHART_TYPE_ICONS: Record<ChartType, typeof LineChart> = {
  line: LineChart,
  bar: BarChart3,
  scatter: ChartScatter,
};

// Two series sharing an identical manually-typed name is a warning, not an error — unlike MCQ/
// Ordering/Keyword/Dropdown's own exact-duplicate-text checks (which are graded-correctness
// hazards, since two identical answer options are ambiguous to score), a chart has no correctness
// concept at all — this is purely a legend/accessible-table clarity issue. Blank labels are
// excluded from comparison (findDuplicateGroups' own `''`-key convention): an untouched label falls
// back to a distinct per-index "Series N" in seriesDisplayLabel, so two blank labels are never
// actually ambiguous the way two identical typed ones are.
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `datavizIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function datavizBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return datavizIssues(toData(block.data) as unknown as DataVizBlockData, ctx);
}

export function datavizIssues(data: DataVizBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  /*
   * A cell names a variable that is not there.
   *
   * The ONLY check a cell needs, and the reason a cell holds a reference rather than an
   * expression: there is nothing to parse, no cycle to find and no arithmetic to fail, so
   * "does that name exist" is the whole of it. A point that will not resolve is DROPPED from the
   * chart, which is the right runtime behaviour and is exactly why it needs saying out loud here
   * -- otherwise a renamed or deleted variable quietly takes points off a curve.
   *
   * Deletion-protection makes the common path unreachable (a cell counts as a use, because the
   * reference is stored as `{name}` text and the usage walker reads every string in block data),
   * so what is left is a reference pasted in from another task, or data predating the variable.
   */
  const known = new Set(variables.map(v => v.name));
  const unknown = dataVizVariableNames(data.series).filter(n => !known.has(n));
  if (unknown.length > 0) {
    issues.push({
      key: 'dataviz-unknown-variable',
      severity: 'error',
      kind: 'defect',
      category: t('blocks.dataviz.issueCategorySeries'),
      detail: t('blocks.dataviz.unknownVariable', { names: unknown.join(', ') }),
    });
  }
  for (const group of findDuplicateGroups(data.series, s => s.label.trim())) {
    issues.push({
      key: `duplicate-series-${group.map(s => s.id).join('-')}`,
      fields: group.map(s => s.id),
      severity: 'warning',
      kind: 'defect',
      category: t('blocks.dataviz.issueCategorySeries'),
      detail: t('blocks.dataviz.duplicateSeriesWarning', { label: group[0]!.label.trim() }),
    });
  }
  /**
   * The teacher chose a numeric X axis and at least one value is not a number.
   *
   * `rowsToSeries` computes `xIsNumeric` as "EVERY row parses", so a single stray value flips the
   * whole axis to categorical — every point re-spaced evenly and re-ordered by first appearance
   * rather than by magnitude. Nothing said so: the chart simply drew something different.
   *
   * The scientific grammar on the X column stops this being TYPED, but three doors stay open: the
   * text-to-number axis switch keeps the old strings deliberately (so switching back is
   * non-destructive), CSV import pastes anything, and data authored before the grammar existed is
   * already stored. This is the check for all three.
   *
   * `xAxisType` has no `'number'` member — undefined IS number mode, which is why the condition
   * reads as an absence.
   */
  if (data.xAxisType === undefined) {
    const offender = data.series
      .flatMap(sr => sr.points)
      .find(pt => typeof pt.x === 'string' && pt.x.trim() !== '');
    if (offender) {
      issues.push({
        key: 'x-axis-not-numeric',
        severity: 'warning',
        kind: 'defect',
        category: t('blocks.dataviz.issueCategoryAxis'),
        detail: t('blocks.dataviz.xAxisNotNumericWarning', { value: `"${String(offender.x)}"` }),
      });
    }
  }

  /**
   * Axis labels, and series names once there is more than one line.
   *
   * A plotted chart whose axes are unnamed cannot be read: the numbers are there and nothing says
   * what they measure. Both are `incompletion` — an absence, not a contradiction — so they stay
   * quiet while the teacher is still building and surface once they move on.
   *
   * Gated on there actually being points. An untouched block's problem is that it has no data, and
   * telling it what is wrong with axes it is not yet plotting is noise.
   *
   * Deliberately NOT a check on the chart TITLE: a chart without one is a legitimate thing to
   * author, and the data-table view is the accessible alternative either way.
   */
  if (data.series.some((sr) => sr.points.length > 0)) {
    if (!data.xAxisLabel?.trim()) {
      issues.push({
        key: 'x-axis-label-missing', fields: ['xAxisLabel'], severity: 'error', kind: 'incompletion',
        category: t('blocks.dataviz.issueCategoryAxis'), detail: t('blocks.dataviz.xAxisLabelMissing'),
      });
    }
    if (!data.yAxisLabel?.trim()) {
      issues.push({
        key: 'y-axis-label-missing', fields: ['yAxisLabel'], severity: 'error', kind: 'incompletion',
        category: t('blocks.dataviz.issueCategoryAxisY'), detail: t('blocks.dataviz.yAxisLabelMissing'),
      });
    }
  }

  // Only with two or more series: `seriesDisplayLabel` falls back to "Series N", which reads fine
  // for a single line and is genuinely ambiguous once a legend has to tell several apart.
  if (data.series.length > 1) {
    const unnamed = data.series.filter((sr) => !sr.label.trim());
    if (unnamed.length) {
      issues.push({
        key: 'series-label-blank', fields: unnamed.map((sr) => sr.id), severity: 'warning', kind: 'incompletion',
        category: t('blocks.dataviz.issueCategorySeries'),
        detail: t('blocks.dataviz.seriesLabelBlank', { count: unnamed.length }),
      });
    }
  }

  return issues;
}

// Same shape as every migrated input block's own top-issue bar (see e.g. ordering-block.tsx's
// orderingCollapsedIssueBar / mcq-block.tsx's mcqBarNode, also ported to image-block.tsx/
// sound-block.tsx) — middle-aligned icon+text, text-sm, matching every other block's collapsed
// warning instead of inventing a new shape for this one.
export default function DataVizBlock({ block, isPreview, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, blocks, onRename }: DataVizBlockProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  /*
   * Memoised so the chart below can be, which is the whole point.
   *
   * `toData` built a fresh object on every render, so `DataVizCanvas`'s own `memo` could never hit
   * and a chart of a few hundred points was rebuilt on every keystroke into any block on the task.
   * Keyed on `block.data`, which keeps its identity while a DIFFERENT block is edited.
   *
   * It also stops the empty-series placeholder minting a new `crypto.randomUUID()` on each render,
   * which was a latent bug of its own: an id that changes every frame is not an id.
   */
  const stored = useMemo(() => toData(block.data), [block.data]);
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !stored.series.some((s) => s.points.length > 0)));
  const taskPreviewRoll = useTaskPreviewRoll();
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  /* The bank and its three-way "which variant" rule, shared with Text, Image and Sound. */
  const {
    bank, leadsGroup, shown, shownVersionId, setTestVersion,
    editorVersionId, setEditorVersion, writeFields, addVariant, removeVariant, renameVariant, removeWarning,
  } = useDisplayVersions(block, DATAVIZ_VERSION_KEYS, onUpdate, {
    isPreview, isEditing, taskPreviewRoll, blocks,
    nameFor: index => t('blocks.versions.scenarioDefault', { index }),
    warnFor: (names: string[]) => t('blocks.scenario.removeWarning', { names: names.join(', ') }),
    base: stored as unknown as Record<string, unknown>,
  });

  /*
   * ABOVE EVERY EARLY RETURN. `isPreview` and friends bail out further down, so declaring these
   * beside `usePortal` meant the hooks ran only on some renders — which is a crash the moment a
   * block flips into Preview, not only a lint complaint.
   */
  const [phoneMode, setPhoneMode] = useState<'edit' | 'test'>('edit');
  /**
   * THE CARD'S OWN ROLL, for the dice on the test bar — the same one Text keeps.
   *
   * A chart has no answer to submit, so until variables reached its cells it had nothing to test
   * and the bar carried only a pager. Now a cell can name a variable, and a chart pinned at one
   * value of each domain cannot be checked against anything: a teacher needs to see the curve
   * under a second roll, and a third.
   *
   * Empty until the card is selected, which is what makes the idle card fall back to the probe.
   */
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  const usePortal = isMobile && !embedded;
  /* Edit/Test on the phone, the surface Text already had. Hidden rather than unmounted so the
     editor keeps its state across a tab flip. See `useDisplayPhoneTest`. */
  const phoneTest = useDisplayPhoneTest({
    block, bank, shownVersionId, setTestVersion, phoneMode, setPhoneMode, enabled: usePortal,
  });
  /* One draw on open, exactly as Text does it: not on every render and not on every variable
     change, since a roll that moved under the teacher while they read the chart would make the
     card unreadable rather than live. */
  useEffect(() => {
    if (isSelected) setRolledValues(rollVariableValues(variables));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  /** Everything below reads the VARIANT IN FORCE, never the block's own vestigial copy. */
  const data = shown as unknown as DataVizBlockData;
  useLayoutEffect(() => {
    if (mobileIsEditing === undefined) return;
    setIsEditing(mobileIsEditing);
  }, [mobileIsEditing]);

  // Mirrors sound-block.tsx's collapsedContentRef pattern — refocuses the collapsed card once the
  // editor closes (this is a hand-rolled display block like Text/Image/Sound, not built on the
  // shared input-block frame).
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const [showCsvImport, setShowCsvImport] = useState(false);
  const [gridResetKey, setGridResetKey] = useState(0);
  const [csvToastMsg, setCsvToastMsg] = useState('');
  // Kept as raw text (not derived from the stored number on every render) so an in-progress
  // keystroke like a trailing "." or a lone "-" isn't immediately stripped back out — mirrors
  // numerical-block.tsx's own fields, which store the typed string directly rather than round-
  // tripping through Number() on every change.
  const [yThresholdText, setYThresholdText] = useState(() => String(data.sonificationThresholds?.y ?? 0));
  const [xThresholdText, setXThresholdText] = useState(() => String(data.sonificationThresholds?.x ?? 0));
  /**
   * The two text mirrors above are the only state this block keeps from its own data — everything
   * else it reads from props each render, which is why it needed no other change to come off the
   * undo remount. They still have to follow a restored value, or an undo of a threshold leaves the
   * number back where it was and the box showing what was typed.
   */
  const thresholdsRestored = useRestoredData<{ y: string; x: string }>({
    raw: block.data,
    isEditing,
    derive: (raw) => {
      const th = (raw as DataVizBlockData | undefined)?.sonificationThresholds;
      return { y: String(th?.y ?? 0), x: String(th?.x ?? 0) };
    },
    current: { y: yThresholdText, x: xThresholdText },
    apply: (next) => { setYThresholdText(next.y); setXThresholdText(next.x); },
  });
  // The point currently sounding during sonification playback, so sighted/low-vision users get a
  // visual anchor alongside the audio — lifted up here since DataVizCanvas/DataVizDisplay and
  // DataVizSonificationPlayer are rendered as siblings, not nested.
  const [highlightedPoint, setHighlightedPoint] = useState<{ x: number | string; y: number } | null>(null);
  // The moving playhead position during sonification playback — lifted for the same reason as
  // highlightedPoint, kept independent of it so the chart keeps visibly progressing through silent
  // stretches (before the first point, between disjoint clusters, after the last one).
  const [playheadX, setPlayheadX] = useState<number | null>(null);
  // Which series (if any) is two-click-armed for removal in the grid below — lifted up here for the
  // same reason as highlightedPoint: DataVizGrid and the pinned DataVizCanvas are siblings, and the
  // whole series should visually stand out on the chart itself while its removal is armed, matching
  // how every other arm-to-delete control in the app highlights the entire thing about to be removed
  // (not just the trash icon).
  const [armedSeriesId, setArmedSeriesId] = useState<string | null>(null);

  /* Through the version being edited when there is a bank, and merged onto the WHOLE stored
     object either way — `onUpdate` replaces `block.data`, so a write naming only what changed
     would delete the bank on the first edit. */
  function set(patch: Partial<DataVizBlockData>) {
    // Marked so this block's own write is never mistaken for someone else's change.
    thresholdsRestored.markEmitted({ ...data, ...patch });
    writeFields(patch as Record<string, unknown>);
  }

  function handleDone() {
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  function handleCsvImport(importedSeries: DataVizBlockData['series'], xAxisType?: DataVizBlockData['xAxisType'], truncation?: { seriesTruncated: boolean; pointsTruncated: boolean }) {
    // xAxisType is always passed explicitly (even as undefined) so a re-import that no longer maps
    // a date column correctly clears a stale 'date' flag from an earlier import, rather than leaving
    // the axis/table/sonification stuck formatting plain numbers or category labels as dates.
    set({ series: importedSeries, sonificationSeriesId: undefined, xAxisType });
    setGridResetKey((n) => n + 1);
    setShowCsvImport(false);
    // The import dialog unmounts as soon as showCsvImport flips false above, so any truncation
    // notice has to be shown from here (this component stays mounted) rather than from the dialog
    // itself — never a silent truncation, the teacher needs to know exactly what got dropped.
    const truncationMessages: string[] = [];
    if (truncation?.seriesTruncated) truncationMessages.push(t('blocks.dataviz.csv.mapping.truncatedSeries', { max: MAX_CSV_SERIES }));
    if (truncation?.pointsTruncated) truncationMessages.push(t('blocks.dataviz.csv.mapping.truncatedPoints', { max: MAX_CSV_POINTS_PER_SERIES }));
    if (truncationMessages.length > 0) setCsvToastMsg(truncationMessages.join(' '));
  }

  function handleThresholdToggle(axis: 'x' | 'y', enabled: boolean) {
    const next = { ...data.sonificationThresholds };
    if (enabled) {
      const startValue = data.sonificationThresholds?.[axis] ?? 0;
      next[axis] = startValue;
      (axis === 'y' ? setYThresholdText : setXThresholdText)(String(startValue));
    } else {
      delete next[axis];
    }
    set({ sonificationThresholds: next });
  }

  function handleThresholdValueChange(axis: 'x' | 'y', value: number) {
    if (isNaN(value)) return;
    set({ sonificationThresholds: { ...data.sonificationThresholds, [axis]: value } });
  }

  const hasData = data.series.some((s) => s.points.length > 0);
  const issues = datavizIssues(data, issueCtx);

  /* Is there anything to test? A bank to page, or a cell with a variable in it. Mirrors Text's
     own `hasTestState`, including its looseness: `hasRollableVariables` asks whether the TASK has
     a random, not whether this chart's particular references are the random ones. */
  const canRoll = dataVizVariableNames(data.series).length > 0 && hasRollableVariables(variables);
  const hasTestState = bank.length > 1 || canRoll;
  /** Rolls the numbers, never the variant. See `DisplayScenarioBar.onReroll`. */
  function rerollVariables() {
    setRolledValues(rollVariableValues(variables));
  }

  /*
   * THE CHART ALWAYS RESOLVES. A chip has no position on an axis, so the display-mode "chips stay
   * chips" rule reaches the editing GRID, where a cell is a field, and stops at the picture.
   *
   * In Preview that is the task's own roll, shared with every other block. Everywhere else it is
   * `probeVariableValues` — the first value of each domain, deterministic — because the idle card
   * needs a number and a random one would redraw the curve on every render. Same thing Numerical's
   * probe does for "is this arithmetic at all": a stable member of the domain, standing in where no
   * real roll exists.
   *
   * NOT `probeRoll`, which this used and which omits equation variables (they have no domain to
   * take a first value FROM). A cell naming a derived variable resolved to nothing and lost its
   * point; see `probeVariableValues` for what that cost.
   *
   * Once the card has been selected it keeps ITS OWN roll, so the dice on the test bar has
   * something to move. It keeps it after deselection too, rather than snapping back to the probe —
   * what the determinism above is protecting against is a value that changes on every RENDER, and a
   * roll that is held is as stable as a probe. The probe is what a card shows before anything has
   * rolled it.
   */
  const testRoll = Object.keys(ownRolledValues).length > 0 ? ownRolledValues : probeVariableValues(variables);
  const resolvedData = resolveDataVizData(data, isPreview ? (taskPreviewRoll ?? {}) : testRoll);

  if (isPreview) {
    // The task editor's "Preview" button is a student-experience simulation, so it must match the
    // block's own display exactly — same DataVizDisplay, same configured always/toggle/slide-out
    // behavior. (The pinned chart shown while editing further down, by contrast, really is just a
    // quick spot-check while entering data — that one intentionally has no table at all.)
    if (!hasData) return null;
    const orientation = chartOrientation(resolvedData, i18n.language, t);
    const orientationIds = orientationIdsFor(block.id, orientation.length);
    return (
      <figure className='flex w-full flex-col gap-3'>
        {/*
          FIRST in the block, because they introduce the chart.

          Rendered from inside the player these arrived after everything they describe, and their
          read-through badges landed on the control row on top of the buttons' own numbers. Here
          they are read before the chart, which is the order a sighted student takes it in — title,
          then what each axis measures and how far it runs.
        */}
        {orientation.map((clause, i) => (
          <p key={orientationIds[i]} id={orientationIds[i]} data-authored='' className='sr-only'>{clause}</p>
        ))}
        <DataVizDisplay data={resolvedData} chartHeight={320} highlightedPoint={highlightedPoint} playheadX={playheadX} />
        <DataVizSonificationPlayer
          series={resolvedData.series}
          selectedSeriesId={data.sonificationSeriesId}
          onSelectSeries={(id) => set({ sonificationSeriesId: id })}
          xAxisType={data.xAxisType}
          orientationIds={orientationIds}
          yAxisLabel={data.yAxisLabel}
          sonificationThresholds={data.sonificationThresholds}
          onPlayingPointChange={setHighlightedPoint}
          onPlayheadChange={setPlayheadX}
        />
      </figure>
    );
  }

  if (!isEditing) {
    // Three distinct states, not two — a chart with real data but no title must never announce as
    // "no data" (it previously did: the old ternary only branched on title, so a titleless-but-
    // populated chart fell into the "no data" wording, which is actively misleading to screen-reader
    // users).
    const ariaLabel = data.title
      ? t('blocks.dataviz.editAriaWithTitle', { title: data.title })
      : hasData
        ? t('blocks.dataviz.editAriaUntitled')
        : t('blocks.dataviz.editAriaNoData');
    const contentProps = {
      tabIndex: 0 as const,
      role: 'button' as const,
      'aria-label': ariaLabel,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isDeleteArmed) { disarmDelete(); return; }
          if (onSelect) onSelect(); else setIsEditing(true);
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.detail === 0) return;
        if (isDeleteArmed) { disarmDelete(); return; }
        if (onSelect) onSelect(); else setIsEditing(true);
      },
    };
    // Both collapsed-card variants show the real DataVizDisplay once there's data — the "main"
    // format is actually the default view (task-content.tsx only drops to the "alternative" card
    // on mobile portrait), so the toggle/slide-out button has to be genuinely testable there, not
    // just in the phone-portrait variant. Its content area then contains DataVizDisplay's real
    // nested toggle/slide-out button, so it can't keep `role='button'` — that role must never
    // contain other interactive descendants (that's what `contentProps` above is for the
    // no-data case, which is always a single inert glance). Same click/keyboard "open the editor"
    // behavior, just without misrepresenting the region as one atomic button when it isn't anymore.
    const dataDisplayContentProps = {
      tabIndex: 0 as const,
      onKeyDown: contentProps.onKeyDown,
      onClick: contentProps.onClick,
    };

    // Passed into DataVizDisplay as sonificationSlot (rendered right under the graph+legend in its
    // own column, not as a sibling below the whole display) so the data table can be stretched to
    // match that combined column's height. Stops both click AND keydown from bubbling — a native
    // button/select fires a bubbling "click" when activated by mouse OR by Enter/Space, but the raw
    // keydown ALSO bubbles independently of that click, so stopping only the click still lets a
    // keyboard Enter/Space on Play (or the series/speed selects) reach the surrounding card's own
    // onKeyDown and wrongly open the editor too.
    const sonificationSlot = (
      <div className='flex justify-center' onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DataVizSonificationPlayer
          series={resolvedData.series}
          selectedSeriesId={data.sonificationSeriesId}
          onSelectSeries={(id) => set({ sonificationSeriesId: id })}
          xAxisType={data.xAxisType}
          yAxisLabel={data.yAxisLabel}
          sonificationThresholds={data.sonificationThresholds}
          onPlayingPointChange={setHighlightedPoint}
          onPlayheadChange={setPlayheadX}
        />
      </div>
    );

    if (isMain) {
      return (
        <BlockCard
          block={block}
          cardRef={blockRef}
          icon={<LineChart className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
          isDeleteArmed={isDeleteArmed}
          isSelected={isSelected}
          isDragOver={isDragOver}
          onDuplicate={triggerDuplicate}
          onDelete={triggerDelete}
          onDisarm={disarmDelete}
        >
          <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
            <div
              ref={collapsedContentRef}
              {...(hasData ? dataDisplayContentProps : contentProps)}
              aria-label={ariaLabel}
              className='relative cursor-pointer p-4 focus-visible:[outline:2px_auto]'
            >
              {/* The same gate Text, Image and Sound use. `leadsGroup` requires a real bank.
                  ABOVE the chart, so the pager keeps its place while the variants it pages
                  change height underneath it. */}
              {(isSelected || leadsGroup) && hasTestState && (
                <DisplayScenarioBar
                  bank={bank}
                  activeId={shownVersionId}
                  onSelect={setTestVersion}
                  /* The dice rolls VARIABLES and never the variant — the pager already steps
                     scenarios, deliberately, and only full Preview draws one at random. See
                     `DisplayScenarioBar.onReroll`.
                     It used to be omitted outright, on the true-at-the-time grounds that this
                     block held no variables at all. A chart cell can name one now. */
                  onReroll={canRoll ? rerollVariables : undefined}
                />
              )}
              {hasData ? (
                <DataVizDisplay data={resolvedData} chartHeight={180} highlightedPoint={highlightedPoint} playheadX={playheadX} armedSeriesId={armedSeriesId} sonificationSlot={sonificationSlot} />
              ) : (
                <p className='text-sm italic text-ws-muted'>{t('blocks.dataviz.empty')}</p>
              )}
            </div>
            {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='border-t border-ws-border' />}
          </div>
        </BlockCard>
      );
    }

    return (
      <BlockPanelCard
        blockId={block.id}
        cardRef={blockRef}
        icon={<LineChart className='h-6 w-6' strokeWidth={2} />}
        title={t('blocks.dataviz.title')}
        isDeleteArmed={isDeleteArmed}
        isSelected={isSelected}
        isDragOver={isDragOver}
        onDuplicate={triggerDuplicate}
        onDelete={triggerDelete}
        onDisarm={disarmDelete}
      >
        <div
          ref={collapsedContentRef}
          {...(hasData ? dataDisplayContentProps : contentProps)}
          aria-label={hasData ? ariaLabel : undefined}
          className={`relative cursor-pointer p-4 focus-visible:[outline:2px_auto] ${issues[0] ? '' : 'rounded-b-xl'}`}
        >
          {hasData ? (
            <DataVizDisplay data={resolvedData} chartHeight={220} highlightedPoint={highlightedPoint} playheadX={playheadX} armedSeriesId={armedSeriesId} sonificationSlot={sonificationSlot} />
          ) : (
            <p className='text-sm italic text-ws-muted'>{t('blocks.dataviz.empty')}</p>
          )}
        </div>
        {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='rounded-b-xl' />}
      </BlockPanelCard>
    );
  }


  const editView = (
    <div className={usePortal
      ? 'fixed inset-x-0 top-0 z-[100] flex flex-col bg-ws-surface h-[100dvh]'
      : 'flex h-full flex-col rounded-xl border border-ws-border bg-ws-surface'
    }>
      <div className={`flex items-center gap-2 border-b border-ws-border bg-ws-hover px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link${!usePortal ? ' rounded-t-xl' : ''}`}>
        <LineChart className='h-6 w-6' strokeWidth={2} />
        <BlockNameField
          blockName={block.name ?? ''}
          titleLabel={t('blocks.dataviz.title')}
          onBlockNameChange={onRename}
          isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
        />
        <button onClick={handleDone} aria-label={t('blocks.dataviz.closeEditor')} className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'>
          <X className='h-5 w-5' />
        </button>
      </div>

      {/*
        The pinned chart, and the whole strip it sits in, is for the editor that CANNOT see the card.

        Pinned above the scrollable setup so a teacher entering data further down can always see —
        and sonification-test — the resulting chart without scrolling back up. Sonification is kept
        deliberately compact (single row, no visible instructions) so the strip stays short enough
        that the setup below still gets real room.

        On desktop and tablet the editor is a side panel and the block's own card stays in the main
        column beside it, chart, legend and player and all — so this was a second copy of a graph
        already on screen, in a narrower column, costing the setup fields the room. Worse than
        redundant: the two sonification players are independent, so both could sound at once.

        The whole strip goes, not just its contents: left standing it is an empty bordered band with
        padding, a rule under nothing. Keyed on `embedded` rather than on phone, because the question
        is whether the card is visible, not how wide the screen is — a non-embedded inline editor
        replaces the card on any width and still needs this.
      */}
      {!embedded && (
      <div className='flex shrink-0 flex-col gap-2 border-b border-ws-border bg-ws-surface p-4'>
        {hasData ? (
          <>
            <DataVizCanvas data={resolvedData} height={160} highlightedPoint={highlightedPoint} armedSeriesId={armedSeriesId} playheadX={playheadX} />
            <DataVizSonificationPlayer
              series={resolvedData.series}
              selectedSeriesId={data.sonificationSeriesId}
              onSelectSeries={(id) => set({ sonificationSeriesId: id })}
              xAxisType={data.xAxisType}
              yAxisLabel={data.yAxisLabel}
              sonificationThresholds={data.sonificationThresholds}
              onPlayingPointChange={setHighlightedPoint}
              onPlayheadChange={setPlayheadX}
            />
          </>
        ) : (
          <p className='py-6 text-center text-sm italic text-ws-muted'>{t('blocks.dataviz.emptyChartPreview')}</p>
        )}
      </div>
      )}

      {phoneTest?.tabs}
      {phoneTest?.pane}
      <div className={`flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-4${phoneTest?.pane ? ' hidden' : ''}`}>
        {/* THE BANK RIDES THE HEADING THIS BLOCK ALREADY HAD.
            Text, Image and Sound open straight into their content, so the bank row brings a
            heading with it. DataViz opens with "Graph Setup", and stacking a second heading
            directly above that would be furniture announcing furniture — so the pager sits in
            the one that was already there, which is also where every input block puts its own.
            Once there is a bank the label becomes the variant's name, as everywhere else. */}
        <DisplayBankRow
          bank={bank}
          label={t('blocks.dataviz.setupHeading')}
          editorVersionId={editorVersionId}
          setEditorVersion={setEditorVersion}
          addVariant={addVariant}
          removeVariant={removeVariant}
          renameVariant={renameVariant}
          removeWarning={removeWarning}
        />

        {/*
          Chart type is a SETUP choice, so it lives under the setup heading with the rest of them.

          It used to sit in the pinned region above, which made sense while a chart was pinned there
          for it to change. With that chart gone on desktop and tablet, the picker was a lone row
          floating over an empty strip, separated by a rule from the settings it belongs with.

          It stays FIRST among them, because it is the one setting that changes what the others
          mean: a bar chart's axis has to reach zero, and a scatter has no line between its points.
        */}
        <div className='flex flex-wrap gap-2'>
          {(['line', 'bar', 'scatter'] as ChartType[]).map((type) => {
            const Icon = CHART_TYPE_ICONS[type];
            const selected = data.chartType === type;
            return (
              <button data-field-id='chartType'
                key={type}
                type='button'
                onClick={() => set({ chartType: type })}
                aria-pressed={selected}
                className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium ${
                  selected ? 'border-ws-primary bg-ws-primary/10 text-ws-primary' : 'border-ws-border bg-ws-btn text-ws-muted hover:bg-ws-hover hover:text-ws-text'
                }`}
              >
                <Icon className='h-4 w-4' />
                {t(`blocks.dataviz.chartType.${type}`)}
              </button>
            );
          })}
        </div>

        <div className='flex flex-col'>
          <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.dataviz.table.displayMode.label')}</label>
          <SelectMenu fieldId='dataTableDisplay'
            value={data.dataTableDisplay ?? 'toggle'}
            onChange={v => set({ dataTableDisplay: v as DataVizBlockData['dataTableDisplay'] })}
            options={[
              { value: 'always',   label: t('blocks.dataviz.table.displayMode.always') },
              { value: 'toggle',   label: t('blocks.dataviz.table.displayMode.toggle') },
              { value: 'slideout', label: t('blocks.dataviz.table.displayMode.slideout') },
            ]}
            ariaLabel={t('blocks.dataviz.table.displayMode.always')}
            className='w-fit' />
        </div>

        <div className='flex flex-col'>
          <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.dataviz.titleLabel')}</label>
          <TextField fieldId='title'
            variant='prose'
            value={data.title ?? ''}
            onChange={v => set({ title: v })}
            placeholder={t('blocks.dataviz.titlePlaceholder')}
            ariaLabel={t('blocks.dataviz.titleLabel')} />
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='flex flex-col'>
            <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.dataviz.xAxisLabel')}</label>
            <TextField fieldId='xAxisLabel'
              variant='prose'
              value={data.xAxisLabel ?? ''}
              onChange={v => set({ xAxisLabel: v })}
              ariaLabel={t('blocks.dataviz.xAxisLabel')} />
          </div>
          <div className='flex flex-col'>
            <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.dataviz.yAxisLabel')}</label>
            <TextField fieldId='yAxisLabel'
              variant='prose'
              value={data.yAxisLabel ?? ''}
              onChange={v => set({ yAxisLabel: v })}
              ariaLabel={t('blocks.dataviz.yAxisLabel')} />
          </div>
        </div>

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-medium text-ws-text'>{t('blocks.dataviz.threshold.label')}</label>

          <div className='flex items-center gap-3'>
            <CheckboxRow
              fieldId='sonificationThresholds.y'
              checked={data.sonificationThresholds?.y !== undefined}
              onToggle={() => handleThresholdToggle('y', data.sonificationThresholds?.y === undefined)}
              label={t('blocks.dataviz.threshold.yReference')}
            />
            {data.sonificationThresholds?.y !== undefined && (
              <NumberField
                grammar='scientific'
                allowNegative
                value={yThresholdText}
                onChange={v => {
                  setYThresholdText(v);
                  const num = Number(v);
                  if (v !== '' && v !== '-' && !isNaN(num)) handleThresholdValueChange('y', num);
                }}
                ariaLabel={t('blocks.dataviz.threshold.yReference')}
                className='w-32' />
            )}
          </div>

          <div className='flex items-center gap-3'>
            <CheckboxRow
              fieldId='sonificationThresholds.x'
              checked={data.sonificationThresholds?.x !== undefined}
              onToggle={() => handleThresholdToggle('x', data.sonificationThresholds?.x === undefined)}
              label={t('blocks.dataviz.threshold.xReference')}
              disabled={!isNumericXAxis(resolvedData.series)}
            />
            {data.sonificationThresholds?.x !== undefined && (
              data.xAxisType === 'date' ? (
                <DateField
                  value={msToIsoDateInput(data.sonificationThresholds.x)}
                  onChange={v => {
                    const ms = isoDateInputToMs(v);
                    if (ms !== null) handleThresholdValueChange('x', ms);
                  }}
                  ariaLabel={t('blocks.dataviz.threshold.xReference')} />
              ) : (
                <NumberField
                grammar='scientific'
                allowNegative
                value={xThresholdText}
                onChange={v => {
                  setXThresholdText(v);
                  const num = Number(v);
                  if (v !== '' && v !== '-' && !isNaN(num)) handleThresholdValueChange('x', num);
                }}
                ariaLabel={t('blocks.dataviz.threshold.xReference')}
                className='w-32' />
              )
            )}
          </div>
        </div>

        <hr className='border-t border-ws-border' />
        <p className='text-sm font-semibold uppercase tracking-wide text-ws-primary'>{t('blocks.dataviz.dataSetupHeading')}</p>

        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-center'>
            <button
              type='button'
              onClick={() => setShowCsvImport(true)}
              className='flex items-center gap-1.5 rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            >
              <Upload className='h-4 w-4' />
              {t('blocks.dataviz.csv.importButton')}
            </button>
          </div>
          {/* Through `set`, not `onUpdate`: the grid hands back a whole DataVizBlockData, and
              writing that straight out would replace the block — bank and all — with one
              variant's worth of content. */}
          <DataVizGrid key={gridResetKey} data={data} onChange={(next) => set(next)} armedSeriesId={armedSeriesId} onArmedSeriesIdChange={setArmedSeriesId} collidingSeriesIds={issueFieldIds(issues)} />
        </div>
      </div>

      {showCsvImport && (
        <DataVizCsvImport hasExistingData={hasData} onImport={handleCsvImport} onClose={() => setShowCsvImport(false)} />
      )}
      {csvToastMsg && <Toast message={csvToastMsg} type='warning' onDismiss={() => setCsvToastMsg('')} />}
    </div>
  );

  return usePortal ? createPortal(editView, document.body) : editView;
}
